import React, { useEffect, useMemo, useRef } from 'react';
import classnames from 'classnames';
import { Bubble, Table, Radio, Checkbox } from 'tea-component';
import DeafaultGame from '@/pages/home/assets/img/common/game-default.svg';
import MobileIcon from '@/pages/strategic/assests/svg/mobile.svg';
import PcIcon from '@/pages/strategic/assests/svg/pc.svg';
import ConsoleIcon from '@/pages/strategic/assests/svg/console.svg';
import Loading from '@intelligence/components/Loading';
import NoData from '@/pages/intelligence/components/NoData';
// @ts-ignore
import * as rbd from 'react-beautiful-dnd';
import type {
  ITimeLineTableProps,
  IPipelineEvent,
  IPipelineProject,
  IEventTypeFilterOption,
  ISelectedEventInfo,
} from '../../type';
import {
  IN_DEV_EVENT_TYPES,
  LIVE_EVENT_TYPES,
  EVENT_TYPE_BG_COLORS,
  CALENDAR_CARD_WIDTH_MAPPING,
  CALENDAR_CARD_WIDTH_MAPPING_FULL,
} from '../../const';
import TextBubbles from '@/pages/home/components/TextBubbles';
import DelayEventBubble from '../DelayEventBubble';
import { hasDelayedEventDates, getDelayCount } from '../../utils/delayDates';
import {
  getEventCardPrimaryLabel,
  getEventCardTypeSuffix,
  getEventTypeLabel,
  hasPipelineEventContent,
} from '../../utils/eventDisplay';
import './index.scss';

const { draggable, scrollable } = Table.addons;

const PROJECT_CELL_WIDTH = 244;
const MONTH_CELL_WIDTH = 120;

const PLATFORM_ICONS: Record<string, string> = {
  mobile: MobileIcon,
  pc: PcIcon,
  console: ConsoleIcon,
};

const TimeLineTableCard: React.FC<ITimeLineTableProps> = (props) => {
  const {
    projects,
    allProjects,
    monthColumns,
    selectedEvent,
    segment,
    onProjectsReorder,
    onEventClick,
    onProjectCoverClick,
    loading = false,
    isEditing = false,
    isFullScreen = false,
    calendarCardSize,
    fullScreenContent,
    selectedFilters,
    setSelectedFilters,
    cascadeValue,
    changeFilterProjects,
    isStudioPage,
  } = props;

  const tableWrapRef = useRef<HTMLDivElement>(null);

  const isInDev = useMemo(() => {
    return segment === 'in_dev';
  }, [segment]);

  // 根据当前浏览器窗口宽度返回屏幕尺寸型号，得到卡片宽度
  const cardWidth = useMemo(() => {
    return isFullScreen
      ? CALENDAR_CARD_WIDTH_MAPPING_FULL[calendarCardSize as any]
      : CALENDAR_CARD_WIDTH_MAPPING[calendarCardSize as any];
  }, [calendarCardSize, isFullScreen]);

  const noDataTextCur = useMemo(() => {
    if (isStudioPage && !allProjects?.length) {
      return isInDev ? 'No in dev projects available' : 'No live titles available';
    }
    return 'No Data';
  }, [isStudioPage, isInDev, allProjects]);

  useEffect(() => {
    if (loading || projects.length === 0) {
      return;
    }

    const cardWidthTemp = cardWidth + 20;
    // 等待 Tea Table DOM 渲染完成后再滚动
    const timer = setTimeout(() => {
      if (!tableWrapRef.current) {
        return;
      }
      const highlightedIndex = monthColumns.findIndex((col) => {
        return col.isHighlighted;
      });
      if (highlightedIndex < 0) {
        return;
      }

      // 查找可滚动容器
      const scrollBody = tableWrapRef.current.querySelector(
        '.dbs-timeline-table-card-table.tea-table--scrollable > div:nth-child(2)',
      ) as HTMLElement | null;
      if (!scrollBody) {
        return;
      }

      const totalScrollWidth = monthColumns.length * cardWidthTemp;
      const viewportWidth = scrollBody.clientWidth - PROJECT_CELL_WIDTH;
      const maxScrollLeft = Math.max(0, totalScrollWidth - viewportWidth);

      let targetScrollLeft: number;

      // 1/2月（index 0 or 1）滚到最左
      if (highlightedIndex <= 1) {
        targetScrollLeft = 0;
      } else {
        // 当前月显示在第2列 => 前一个月为第1列
        targetScrollLeft = (highlightedIndex - 1) * cardWidthTemp;
      }

      // 不超过最大可滚动距离
      targetScrollLeft = Math.min(targetScrollLeft, maxScrollLeft);

      scrollBody.scrollLeft = targetScrollLeft;
    }, 100);

    return () => {
      clearTimeout(timer);
    };
  }, [monthColumns, loading, projects, cardWidth]);

  // 拖拽时保持 td 宽度与未拖拽状态一致
  useEffect(() => {
    if (!isEditing || !tableWrapRef.current) {
      return;
    }

    let savedWidths: number[] = [];

    const handleMouseDown = (e: MouseEvent) => {
      const tr = (e.target as HTMLElement).closest('tr');
      if (!tr) {
        return;
      }
      const tds = tr.querySelectorAll('td');
      savedWidths = Array.from(tds).map((td) => {
        return td.getBoundingClientRect().width;
      });
    };

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type !== 'attributes' || mutation.attributeName !== 'class') {
          continue;
        }
        const el = mutation.target as HTMLElement;
        if (el.tagName === 'TR' && el.classList.contains('is-dragging')) {
          const tds = el.querySelectorAll('td');
          tds.forEach((td, i) => {
            const tdEl = td as HTMLElement;
            if (savedWidths[i]) {
              tdEl.style.width = `${savedWidths[i]}px`;
              tdEl.style.minWidth = `${savedWidths[i]}px`;
              tdEl.style.maxWidth = `${savedWidths[i]}px`;
            }
          });
        }
      }
    });

    const tableEl = tableWrapRef.current.querySelector('table');
    if (tableEl) {
      tableEl.addEventListener('mousedown', handleMouseDown);
      const tbody = tableEl.querySelector('tbody');
      if (tbody) {
        observer.observe(tbody, {
          attributes: true,
          subtree: true,
          attributeFilter: ['class'],
        });
      }
    }

    return () => {
      if (tableEl) {
        tableEl.removeEventListener('mousedown', handleMouseDown);
      }
      observer.disconnect();
    };
  }, [isEditing]);

  const yearLabel = useMemo(() => {
    if (monthColumns.length === 0) {
      return '';
    }
    const firstYear = monthColumns[0].year;
    const lastYear = monthColumns[monthColumns.length - 1].year;
    return firstYear === lastYear ? String(firstYear) : `${firstYear}-${lastYear}`;
  }, [monthColumns]);

  const isAllSelected = selectedFilters.includes('all') || selectedFilters.length === 0;

  const handleCategoryAllChange = () => {
    if (!isAllSelected) {
      changeFilterProjects?.(cascadeValue, ['all']);
      setSelectedFilters?.(['all']);
    }
  };

  const handleCategoryAllChangeCur = (value: string[]) => {
    const eventTypes = isInDev ? IN_DEV_EVENT_TYPES : LIVE_EVENT_TYPES;
    if (value.length === eventTypes.length || value.length === 0) {
      changeFilterProjects?.(cascadeValue, ['all']);
      setSelectedFilters?.(['all']);
    } else {
      changeFilterProjects?.(cascadeValue, value);
      setSelectedFilters?.(value);
    }
  };

  const getTooltipContent = (evt: IPipelineEvent): React.ReactNode => {
    if (hasDelayedEventDates(evt)) {
      return <DelayEventBubble evt={evt} />;
    }

    const label = getEventCardPrimaryLabel(evt);
    const typeLabel = getEventCardTypeSuffix(evt);
    const typeSuffix = typeLabel ? ` (${typeLabel})` : '';
    const dateValue = evt.start_date || evt.date;

    return (
      <div className="dbs-pipeline-event-bubble__title">
        <span className="dbs-pipeline-event-bubble__title-name">
          {label}
          {typeSuffix}
        </span>
        {dateValue ? (
          <span className="dbs-pipeline-event-bubble__title-date">
            {` (${dateValue})`}
          </span>
        ) : null}
      </div>
    );
  };

  const isEventSelected = (
    evt: IPipelineEvent,
    proj: IPipelineProject,
    sel: ISelectedEventInfo | null | undefined,
  ): boolean => {
    if (!sel) {
      return false;
    }
    return sel.projectName === proj.project && sel.date === evt.date && sel.eventName === evt.milestone;
  };

  const renderEventCards = (events: IPipelineEvent[], proj: IPipelineProject) => {
    if (!events || events.length === 0) {
      return <div className="dbs-timeline-table-event-empty">/</div>;
    }

    // 空态占位 event（milestone 为空）渲染为 empty 样式
    if (!hasPipelineEventContent(events[0])) {
      const evt = events[0];
      return (
        <div
          className={classnames('dbs-timeline-table-event-empty', {
            'is-selected': isEventSelected(evt, proj, selectedEvent),
          })}
          onClick={() => {
            if (onEventClick) {
              onEventClick(evt, proj);
            }
          }}
        >
          /
        </div>
      );
    }

    return (
      <div
        className={classnames('dbs-timeline-table-events-wrapper', {
          'dbs-timeline-table-events-wrapper-list': events.length >= 2,
        })}
      >
        {events.slice(0, 2)?.map((evt) => {
          const bgColor = EVENT_TYPE_BG_COLORS[evt.type] || 'rgba(242, 243, 245, 0.5)';
          const isDelayed = hasDelayedEventDates(evt);
          const typeLabel = getEventCardTypeSuffix(evt);

          return (
            <Bubble
              key={String(evt.id)}
              dark={isDelayed}
              overlayClassName={
                isDelayed ? 'dbs-timeline-delay-event-bubble' : undefined
              }
              content={getTooltipContent(evt)}
              popupContainer={isFullScreen ? (fullScreenContent?.current as any) : undefined}
            >
              <div
                className={classnames('dbs-timeline-table-event-card', {
                  'is-selected': isEventSelected(evt, proj, selectedEvent),
                })}
                style={{ background: bgColor }}
                onClick={() => {
                  if (onEventClick) {
                    onEventClick(evt, proj);
                  }
                }}
              >
                <span className="dbs-timeline-table-event-card__label">
                  {isDelayed ? (
                    <span
                      className="dbs-timeline-table-event-card__delay-icon"
                      aria-label="Delayed"
                    >
                      D
                    </span>
                  ) : null}
                  {getEventCardPrimaryLabel(evt)}
                  {typeLabel ? (
                    <span className="dbs-timeline-table-event-card__milestone">
                      {' '}({typeLabel})
                    </span>
                  ) : null}
                </span>
              </div>
            </Bubble>
          );
        })}
      </div>
    );
  };

  // Flatten all events for a project, keep only those with delay history.
  // Deduplicate by type+name+milestone rather than id — ids are not reliably unique
  // across events, so colliding ids would silently drop distinct delayed events.
  const getProjectDelayedEvents = (proj: IPipelineProject): IPipelineEvent[] => {
    const seen = new Set<string>();
    const result: IPipelineEvent[] = [];
    Object.values(proj.events).flat().forEach(evt => {
      const key = `${evt.type}_${evt.milestone || ''}_${getEventTypeLabel(evt.type)}`;
      if (hasDelayedEventDates(evt) && !seen.has(key)) {
        seen.add(key);
        result.push(evt);
      }
    });
    return result;
  };

  const renderProjectCell = (proj: IPipelineProject) => {
    const cover = proj.cover || DeafaultGame;
    let statusText = '';

    if (proj.status === 'delayed') {
      statusText = 'Delayed';
    } else if (proj.status === 'on_track') {
      statusText = 'On Track';
    }

    const statusClassName = proj.status === 'delayed' ? '--delayed' : '--on-track';

    const delayedEvents = getProjectDelayedEvents(proj);
    const totalDelays = delayedEvents.reduce((sum, evt) => sum + getDelayCount(evt.dates || []), 0);

    const handleCoverClick = (event: React.MouseEvent): void => {
      if (!onProjectCoverClick) {
        return;
      }
      event.stopPropagation();
      onProjectCoverClick(proj);
    };

    return (
      <div className="dbs-timeline-table-project-cell">
        <div className="dbs-timeline-table-project-info">
          <img
            className={classnames('dbs-timeline-table-project-info-cover', {
              'dbs-timeline-table-project-info-cover--clickable': !!onProjectCoverClick,
            })}
            src={cover}
            alt={proj.project}
            onClick={handleCoverClick}
            onError={(errorEvent: any) => {
              const currentTarget = errorEvent.currentTarget as HTMLImageElement;
              currentTarget.src = DeafaultGame;
            }}
          />
          <div className="dbs-timeline-table-project-info-detail">
            <div
              className={classnames('dbs-timeline-table-project-info-name', {
                'dbs-timeline-table-project-info-name--clickable': !!onProjectCoverClick,
              })}
              onClick={handleCoverClick}
            >
              <TextBubbles text={proj.project} />
            </div>
            <div className="dbs-timeline-table-project-info-studio">
              {proj.mgmt_platform && proj.mgmt_platform?.length > 0 && (
                <>
                  <div className="dbs-timeline-table-project-info-studio-plat-icon">
                    {proj.mgmt_platform?.map((platform) => {
                      const icon = PLATFORM_ICONS[platform];
                      if (!icon) {
                        return null;
                      }
                      return <img key={platform} src={icon} alt={platform} />;
                    })}
                  </div>
                </>
              )}
              <TextBubbles text={proj.studio} />
            </div>
            {isInDev && statusText && (
              <div
                className={`dbs-timeline-table-project-info-status dbs-timeline-table-project-info-status${statusClassName}`}
              >
                {statusText}
              </div>
            )}
            {totalDelays > 0 && (
              <Bubble
                dark
                placement="right"
                overlayClassName="dbs-timeline-delay-event-bubble"
                content={
                  <div className="dbs-timeline-delay-summary">
                    {delayedEvents.map((evt, i) => (
                      <React.Fragment key={String(evt.id)}>
                        {i > 0 && <div className="dbs-timeline-delay-summary__divider" />}
                        <DelayEventBubble evt={evt} />
                      </React.Fragment>
                    ))}
                  </div>
                }
                popupContainer={isFullScreen ? (fullScreenContent?.current as any) : undefined}
              >
                <div className="dbs-timeline-table-project-info-delay-badge">
                  {totalDelays === 1 ? '1 Delay' : `${totalDelays} Delays`}
                </div>
              </Bubble>
            )}
          </div>
        </div>
      </div>
    );
  };

  const columns = useMemo(() => {
    const baseColumns: any[] = [];

    if (isEditing) {
      baseColumns.push({
        key: '__draggable_addon__',
        width: 26,
        fixed: 'left',
        header: null,
        render: () => {
          return null;
        },
      });
    }

    baseColumns.push({
      key: 'project',
      header: <div className="dbs-timeline-table-year-cell">{yearLabel}</div>,
      width: isFullScreen ? cardWidth + 20 : PROJECT_CELL_WIDTH,
      fixed: 'left',
      className: 'dbs-timeline-table-column-project',
      render: (record: IPipelineProject) => {
        return renderProjectCell(record);
      },
    });

    const monthTableColumns = monthColumns?.map((col, index) => {
      return {
        key: col.key,
        width: cardWidth + 20,
        // width: MONTH_CELL_WIDTH,
        className: classnames('dbs-timeline-table-column-month', {
          'is-highlighted': col.isHighlighted,
        }),
        header: (
          <div
            style={{ width: cardWidth }}
            className={classnames('dbs-timeline-table-month-cell', {
              'is-highlighted': col.isHighlighted,
              'is-first': index === 0,
              'is-last': index === monthColumns.length - 1,
            })}
          >
            <div className="dbs-timeline-table-month-line" />
            <div className="dbs-timeline-table-month-label">{col.label}</div>
          </div>
        ),
        render: (record: IPipelineProject) => {
          const events = record.events[col.key] || [];
          return (
            <div
              style={{ width: cardWidth }}
              className={classnames('dbs-timeline-table-event-cell', {
                'is-highlighted': col.isHighlighted,
                'is-empty': events.length === 0,
              })}
            >
              {renderEventCards(events, record)}
            </div>
          );
        },
      };
    });

    return [...baseColumns, ...monthTableColumns];
  }, [monthColumns, segment, yearLabel, selectedEvent, isEditing, calendarCardSize, isFullScreen, cardWidth]);

  const addonsCur = useMemo(() => {
    const addonsSrollable = [
      scrollable({
        minWidth: isFullScreen ? 'auto' : PROJECT_CELL_WIDTH + monthColumns.length * MONTH_CELL_WIDTH,
        maxHeight: isFullScreen ? 'calc(100vh - 60px)' : 'calc(100vh - 340px)',
      }),
    ];
    const addonsDraggable = [
      draggable({
        module: rbd,
        targetColumnKey: '__draggable_addon__',
        onDragEnd: (records: IPipelineProject[]) => {
          if (onProjectsReorder) {
            onProjectsReorder(records);
          }
        },
        hideHandler: !isEditing,
      }),
    ];
    return [...addonsSrollable, ...addonsDraggable];
  }, [monthColumns.length, isEditing, isFullScreen]);

  const filterOptionList = useMemo(() => {
    return isInDev ? IN_DEV_EVENT_TYPES : LIVE_EVENT_TYPES;
  }, [segment]);

  return (
    <div
      className={classnames('dbs-timeline-table dbs-timeline-table-card', {
        'dbs-timeline-table-card--fullscreen': isFullScreen,
      })}
    >
      <div className="dbs-timeline-table-title-row">
        <h3>Pipeline Calendar</h3>
        {!loading && allProjects?.length > 0 && (
          <div className="dbs-timeline-table-filters">
            <div className="dbs-timeline-table-filters-indev">
              <Radio name="all" value={isAllSelected} onChange={handleCategoryAllChange}>
                All
              </Radio>
              <Checkbox.Group value={isAllSelected ? [] : selectedFilters} onChange={handleCategoryAllChangeCur}>
                {filterOptionList?.map((filterOption: IEventTypeFilterOption) => {
                  return (
                    <Checkbox key={filterOption.key} name={filterOption.key}>
                      <div className="dbg-calendar-operator-check">
                        <i
                          className="dbg-schedule-calendar-legend-icon"
                          style={{ backgroundColor: filterOption.color }}
                        ></i>
                        {filterOption.label}
                      </div>
                    </Checkbox>
                  );
                })}
              </Checkbox.Group>
            </div>
          </div>
        )}
      </div>
      <div
        className={classnames('dbs-timeline-table-scroll', {
          'dbs-timeline-table-scroll-editing': isEditing,
          'dbs-timeline-table-scroll--fullscreen': isFullScreen,
        })}
        ref={tableWrapRef}
      >
        {loading && <Loading classNameValue="dbs-loading" style={{ height: '643px' }} />}
        {!loading && projects?.length === 0 && <NoData style={{ height: '642px' }} text={noDataTextCur} />}
        {!loading && projects?.length > 0 && (
          <Table
            className="dbs-timeline-table-card-table"
            bordered={false}
            records={projects}
            recordKey="id"
            columns={columns}
            addons={addonsCur}
            {...(isEditing
              ? {
                editable: true,
                hideButtons: true,
                userPermissions: { canEdit: true },
              }
              : {})}
            verticalTop
          />
        )}
      </div>
    </div>
  );
};

export default TimeLineTableCard;
